import {
    useQuery as useQueryOriginal,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    withSafeUsersArray,
    withSafeUsers,
    findUserBaseFields,
} from '@tickitacka/shared';

/**
 * UserBase 필드를 자동으로 안전하게 처리하는 Query 훅
 *
 * 일반 useQuery와 동일하게 사용하되, 반환 데이터의 UserBase 타입 필드를
 * 자동으로 감지하여 탈퇴 유저를 안전하게 처리합니다.
 *
 * 배열과 단일 객체 모두 지원합니다.
 *
 * @template TQueryFnData - 쿼리 함수가 반환하는 데이터 타입
 * @template TError - 에러 타입 (기본값: Error)
 * @template TData - select로 변환된 최종 데이터 타입 (기본값: TQueryFnData)
 * @template TQueryKey - 쿼리 키 타입
 *
 * @example
 * // 배열 데이터 (자동 추론)
 * const { data } = useSafeQuery({
 *   queryKey: ['actionItems', meetingId],
 *   queryFn: () => getActionItems(meetingId),  // returns ActionItem[]
 * });
 *
 * @example
 * // 단일 객체 데이터
 * const { data } = useSafeQuery({
 *   queryKey: ['meeting', meetingId],
 *   queryFn: () => getMeeting(meetingId),  // returns Meeting | null
 *   userFields: ['member', 'leader'],
 * });
 *
 * @example
 * // 명시적 필드 지정
 * const { data } = useSafeQuery({
 *   queryKey: ['feedbacks', userId],
 *   queryFn: () => getFeedbacks(userId),
 *   userFields: ['sender', 'receiver'],
 * });
 */

// 오버로드 1: 배열 데이터
export function useSafeQuery<
    TQueryFnData extends object[],
    TError = Error,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[],
>(
    options: Omit<
        UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
        'select'
    > & {
        userFields?: (keyof TQueryFnData[0])[];
        selectTransform?: (safeData: TQueryFnData) => TData;
    }
): UseQueryResult<TData, TError>;

// 오버로드 2: 단일 객체 (null 가능)
export function useSafeQuery<
    TQueryFnData extends object | null,
    TError = Error,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[],
>(
    options: Omit<
        UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
        'select'
    > & {
        userFields?: TQueryFnData extends object
            ? (keyof TQueryFnData)[]
            : never;
        selectTransform?: (safeData: TQueryFnData) => TData;
    }
): UseQueryResult<TData, TError>;

// 구현
export function useSafeQuery<
    TQueryFnData,
    TError = Error,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[],
>(
    options: Omit<
        UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
        'select'
    > & {
        userFields?: unknown;
        selectTransform?: (safeData: TQueryFnData) => TData;
    }
): UseQueryResult<TData, TError> {
    const { userFields, selectTransform, ...queryOptions } = options;
    const { t } = useTranslation('common');

    return useQueryOriginal({
        ...queryOptions,
        select: data => {
            let safeData: TQueryFnData;

            // 배열인 경우
            if (Array.isArray(data)) {
                type DataItemType = TQueryFnData extends (infer U)[]
                    ? U extends object
                        ? U
                        : never
                    : never;
                safeData = withSafeUsersArray<DataItemType>(
                    data as DataItemType[],
                    t,
                    userFields
                        ? { userFields: userFields as (keyof DataItemType)[] }
                        : undefined
                ) as TQueryFnData;
            }
            // 단일 객체인 경우
            else if (data && typeof data === 'object') {
                // userFields가 명시되지 않은 경우 자동 감지
                const fieldsToProcess =
                    userFields && Array.isArray(userFields)
                        ? (userFields as string[])
                        : findUserBaseFields(data as Record<string, unknown>);

                safeData = withSafeUsers(
                    data as Record<string, unknown>,
                    fieldsToProcess,
                    t
                ) as TQueryFnData;
            }
            // null이나 기타
            else {
                safeData = data;
            }

            // 추가 변환이 있으면 적용
            if (selectTransform) {
                return selectTransform(safeData);
            }

            return safeData as unknown as TData;
        },
    } as UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>);
}
